import type { FC } from "react";
import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";

import GameIcon from "assets/icons/colored/game.svg?react";
import TradingIcon from "assets/icons/colored/trading.svg?react";
import ChipsTokenLogoUrl from "assets/images/chips.webp";
import { useUser } from "shared/hooks";
import { MenuListPositionOpts } from "shared/types/componentTypes";
import { formatMoney } from "utils/formatMoney";

import { makeId } from "utils/serviceUtils/helpers";

import PopoverBox from "../../PopoverBox";

interface BalancePopoverProps {
	show: boolean;
	balanceWrapper: React.RefObject<null | HTMLDivElement>;
	onClose: () => void;
}

const BalancePopover: FC<BalancePopoverProps> = ({ show, balanceWrapper, onClose }) => {
	const { t } = useTranslation();

	const { getData: getUserData } = useUser();
	const { user, userCurrency } = getUserData();

	const balanceList = useMemo(
		() => [
			{
				id: makeId(),
				label: `${t("gaming_balance")} (${t("gaming_balance_short")})`,
				value: formatMoney(user?.balance || 0, { currency: userCurrency }),
				icon: <GameIcon />
			},
			{
				id: makeId(),
				label: `${t("trading_balance")} (${t("trading_balance_short")})`,
				// TODO: integrate logic
				value: formatMoney(8200.54, { currency: userCurrency }),
				icon: <TradingIcon />
			},
			{
				id: makeId(),
				label: `${t("chips_balance")} (${t("chips_balance_short")})`,
				// TODO: integrate logic
				value: formatMoney(183000, { currency: userCurrency }),
				iconUrl: ChipsTokenLogoUrl
			}
		],
		[user, userCurrency, t]
	);

	return (
		<PopoverBox
			anchorEl={balanceWrapper.current}
			onClose={onClose}
			show={show}
			popoverPosition={MenuListPositionOpts.TOP_LEFT}
			contentPosition={MenuListPositionOpts.BOTTOM_LEFT}
			eclipse={false}
			customMinWidth={balanceWrapper.current?.clientWidth ? balanceWrapper.current.clientWidth : undefined}
			offset={[8]}
		>
			<div className="bg-red-600 rounded-xl p-1 grid grid-cols-s gap-1">
				{balanceList.map(balance => (
					<div key={balance.id} className="w-full py-1 px-2 flex flex-col gap-1 bg-red-dark rounded-lg">
						<div className="w-full flex gap-2">
							<div className="w-4 h-4 flex items-center justify-center">
								{balance.icon ?? <img src={balance.iconUrl} alt={balance.label} />}
							</div>
							<p className="font-semibold text-xs leading-5 text-white">{balance.label}</p>
						</div>
						<p className="font-semibold text-base leading-6 text-yellow-400">{balance.value}</p>
					</div>
				))}
			</div>
		</PopoverBox>
	);
};

export default BalancePopover;
